/**
 * Reine Auflösung der Berichtsstruktur aus der Anzeige-Konfiguration: welche Ebenen in welcher
 * Reihenfolge dargestellt werden, welche Spalten je Ebene sichtbar sind und wie sie heißen.
 *
 * Diese Funktionen sind die gemeinsame Quelle für **beide** Berichtsansichten – die Spaltenansicht
 * (`bic-result`) und die Kartenansicht. Sie enthalten deshalb keine UI-Zustände (Selektion,
 * Aufklappzustand) und kein Angular; die Komponenten delegieren lediglich an sie.
 */

import {NormalizedReport} from './normalized-report.model';
import {ReportDisplayConfig, ReportDisplayConfigLevel} from './report-display-config';

/** Ebenen des generischen Renderers (KI/Hybrid/generisch). */
export type ReportLevelKey = 'files' | 'structure' | 'rules' | 'detail';
/** Ebenen des IDS-Renderers. */
export type IdsLevelKey = 'files' | 'specifications' | 'requirements';

/** Fallback-Überschriften je Ebene, wenn die Config kein Label vorgibt. */
export const LEVEL_DEFAULT_LABELS: Record<string, string> = {
  files: 'Dateien',
  structure: 'Strukturebene',
  rules: 'Prüfregeln',
  detail: 'Ergebnis',
  specifications: 'Spezifikationen',
  requirements: 'Anforderungen',
};

/** Fallback-Überschriften je Spalten-Key. */
export const COLUMN_DEFAULT_LABELS: Record<string, string> = {
  fileName: 'Dateiname',
  fileFormat: 'Dateiformat',
  rating: 'Ergebnis',
  name: 'Prüfregel',
  shortCode: 'Kürzel',
  description: 'Beschreibung',
  identifikator: 'Identifikator',
  applicability: 'Anwendbarkeit',
  elements: 'Bauteile',
  result: 'Ergebnis',
};

/** Default-Spalten der IDS-Spezifikationsebene in Anzeigereihenfolge. */
export const IDS_SPEC_DEFAULT_COLUMNS = ['name', 'applicability', 'elements', 'result'];

function levelsOf(config: ReportDisplayConfig | null | undefined): ReportDisplayConfigLevel[] | undefined {
  const levels = config?.levels;
  return levels && levels.length > 0 ? levels : undefined;
}

function normalizeLevel4(key: string): ReportLevelKey | null {
  return key === 'files' || key === 'structure' || key === 'rules' || key === 'detail' ? key : null;
}

/** Normalisiert einen konfigurierten Ebenen-Key auf die IDS-Ebenen. */
export function normalizeIdsLevelKey(levelKey: string): IdsLevelKey | null {
  if (levelKey === 'files' || levelKey === 'structure') return 'files';
  if (levelKey === 'specifications' || levelKey === 'rules') return 'specifications';
  if (levelKey === 'requirements' || levelKey === 'detail') return 'requirements';
  return null;
}

/**
 * Ebenen-Reihenfolge des generischen Renderers; das Ergebnis (`detail`) steht immer zuletzt.
 *
 * Die Strukturebene ist datengetrieben, nicht konfigurationsgetrieben:
 * - Die Daten liefern keine Positionen → die Ebene entfällt, auch wenn sie konfiguriert ist.
 * - Die Daten liefern Positionen → die Ebene wird gezeigt, auch wenn sie in der Config fehlt
 *   (dann hinter den Dateien eingefügt).
 *
 * Der zweite Fall ist kein Sonderfall für Altberichte: die gespeicherte Ebenenliste eines myChecks
 * stammt aus der Prüfebene des Regelsets zum Zeitpunkt des Speicherns. Wird die Prüfebene des Sets
 * später auf Positionen umgestellt, ist die gespeicherte Reihenfolge veraltet – die Config strikt
 * zu nehmen hieße, die Positionen samt ihrer Gruppierung stillschweigend zu verschlucken. Eine
 * Config ohne Strukturebene bei Positionsdaten ist immer veraltet und nie eine bewusste Auswahl:
 * der Builder bietet diese Kombination gar nicht an (`validLevelOrderings`).
 */
export function resolveRenderedLevelKeys(
  report: NormalizedReport | null | undefined,
  config: ReportDisplayConfig | null | undefined,
): ReportLevelKey[] {
  const configured = levelsOf(config);
  const hasStructures = (report?.groups ?? []).some(group => (group.structures?.length ?? 0) > 0);

  let order: ReportLevelKey[] = [];
  if (configured) {
    const seen = new Set<ReportLevelKey>();
    for (const level of configured) {
      const key = normalizeLevel4(level.key);
      if (key && !seen.has(key)) {
        seen.add(key);
        order.push(key);
      }
    }
  }
  if (order.length === 0) {
    order = hasStructures ? ['files', 'structure', 'rules', 'detail'] : ['files', 'rules', 'detail'];
  }
  if (!hasStructures) order = order.filter(key => key !== 'structure');

  const nonDetail = order.filter(key => key !== 'detail');
  if (hasStructures && !nonDetail.includes('structure')) {
    const filesIndex = nonDetail.indexOf('files');
    nonDetail.splice(filesIndex >= 0 ? filesIndex + 1 : 0, 0, 'structure');
  }

  // Bei nur einer geprüften Datei entfällt die Dateiauswahl-Ebene (Dateiname steht stattdessen im
  // Overview-Header), analog zu resolveIdsLevelKeys.
  if ((report?.groups?.length ?? 0) <= 1) {
    const filesIndex = nonDetail.indexOf('files');
    if (filesIndex >= 0) nonDetail.splice(filesIndex, 1);
  }

  return [...nonDetail, 'detail'];
}

/** Nicht-Detail-Ebenen in Anzeigereihenfolge (= die Gruppierungsebenen). */
export function resolveRenderedColumnKeys(
  report: NormalizedReport | null | undefined,
  config: ReportDisplayConfig | null | undefined,
): Array<Exclude<ReportLevelKey, 'detail'>> {
  return resolveRenderedLevelKeys(report, config)
    .filter(key => key !== 'detail') as Array<Exclude<ReportLevelKey, 'detail'>>;
}

/**
 * Ebenen-Reihenfolge des IDS-Renderers. Bei nur einer geprüften Datei entfällt die
 * Dateiauswahl-Ebene.
 */
export function resolveIdsLevelKeys(
  report: NormalizedReport | null | undefined,
  config: ReportDisplayConfig | null | undefined,
): IdsLevelKey[] {
  const configured = levelsOf(config);
  const fallback: IdsLevelKey[] = ['files', 'specifications', 'requirements'];

  let ordered: IdsLevelKey[];
  if (!configured) {
    ordered = [...fallback];
  } else {
    const seen = new Set<IdsLevelKey>();
    ordered = [];
    for (const level of configured) {
      const normalized = normalizeIdsLevelKey(level.key);
      if (!normalized || seen.has(normalized)) continue;
      seen.add(normalized);
      ordered.push(normalized);
    }
    if (ordered.length === 0) ordered = [...fallback];
  }

  if ((report?.groups?.length ?? 0) <= 1) ordered = ordered.filter(key => key !== 'files');
  return ordered;
}

/**
 * Config-Key, unter dem die Dateiebene konfiguriert ist. Manche myChecks konfigurieren die oberste
 * Ebene als `structure` statt `files`; die Anzeige behandelt sie dann als Dateiebene.
 */
export function activeFileLevelConfigKey(
  config: ReportDisplayConfig | null | undefined,
): 'files' | 'structure' {
  const configured = config?.levels ?? [];
  const firstMatching = configured.find(level => level.key === 'structure' || level.key === 'files');
  return firstMatching?.key === 'structure' ? 'structure' : 'files';
}

/** Config-Key einer Anzeige-Ebene (Dateiebene ggf. als `structure` konfiguriert). */
export function configKeyForLevel(
  config: ReportDisplayConfig | null | undefined,
  levelKey: string,
): string {
  return levelKey === 'files' ? activeFileLevelConfigKey(config) : levelKey;
}

/**
 * Alias-Paare zwischen generischer und IDS-Benennung derselben Ebene. Eine als `specifications`
 * konfigurierte Ebene ist dieselbe wie `rules`; die oberste Ebene ist je myCheck als `files` oder
 * als `structure` gespeichert.
 */
const LEVEL_ALIASES: Record<string, string[]> = {
  files: ['files', 'structure'],
  structure: ['structure'],
  rules: ['rules', 'specifications'],
  specifications: ['specifications', 'rules'],
  detail: ['detail', 'requirements'],
  requirements: ['requirements', 'detail'],
};

/** Die Config-Keys, unter denen eine Ebene gespeichert sein kann (in Prüfreihenfolge). */
export function levelConfigKeys(levelKey: string): string[] {
  return LEVEL_ALIASES[levelKey] ?? [levelKey];
}

/** Findet die konfigurierte Ebene; berücksichtigt die Alias-Paare (siehe {@link levelConfigKeys}). */
export function findConfiguredLevel(
  config: ReportDisplayConfig | null | undefined,
  levelKey: string,
): ReportDisplayConfigLevel | null {
  const levels = levelsOf(config);
  if (!levels) return null;
  for (const key of levelConfigKeys(levelKey)) {
    const match = levels.find(level => level.key === key);
    if (match) return match;
  }
  return null;
}

/** Findet die erste konfigurierte Ebene aus einer Liste von Kandidaten-Keys (IDS-Aliasse). */
export function findConfiguredLevelByKeys(
  config: ReportDisplayConfig | null | undefined,
  levelKeys: string[],
): ReportDisplayConfigLevel | null {
  const levels = levelsOf(config);
  if (!levels) return null;
  for (const key of levelKeys) {
    const match = levels.find(level => level.key === key);
    if (match) return match;
  }
  return null;
}

/** Ob eine Spalte einer Ebene sichtbar ist; ohne Config bzw. ohne Eintrag gilt `defaultVisible`. */
export function isColumnVisible(
  config: ReportDisplayConfig | null | undefined,
  levelKey: string,
  columnKey: string,
  defaultVisible: boolean,
): boolean {
  const levels = levelsOf(config);
  if (!levels) return defaultVisible;

  const level = levels.find(candidate => candidate.key === levelKey);
  if (!level) return defaultVisible;

  const column = level.columns?.find(candidate => candidate.key === columnKey);
  if (!column) return defaultVisible;

  return column.visible !== false;
}

/** Wie {@link isColumnVisible}, aber über mehrere Kandidaten-Keys derselben Ebene (IDS-Aliasse). */
export function isColumnVisibleByKeys(
  config: ReportDisplayConfig | null | undefined,
  levelKeys: string[],
  columnKey: string,
  defaultVisible: boolean,
): boolean {
  for (const key of levelKeys) {
    const levelExists = config?.levels?.some(level => level.key === key);
    if (levelExists) return isColumnVisible(config, key, columnKey, defaultVisible);
  }
  return defaultVisible;
}

/**
 * Ob der Status einer Ebene dargestellt wird. Ersetzt die früher je Ebene einzeln implementierten
 * `showDetailStatus` / `showSpecStatus` / `showReqStatus` – es ist dieselbe Frage. Die
 * Kandidaten-Keys deckeln die Alias-Paare zwischen generischer und IDS-Benennung ab.
 */
export function isLevelStatusVisible(
  config: ReportDisplayConfig | null | undefined,
  levelKey: string,
): boolean {
  return isColumnVisibleByKeys(config, levelConfigKeys(levelKey), 'status', true);
}

/**
 * Ob eine Spalte einer Ebene sichtbar ist – alias-aware und mit Default für Spalten, die eine
 * (ältere) Konfiguration überhaupt nicht kennt. Genau das braucht die Kartenansicht für Werte, die
 * der Entwurf vorsieht, die aber erst nachträglich konfigurierbar wurden (z. B. `description`):
 * ohne Eintrag gilt der Default, ein explizites Ausblenden wird respektiert.
 */
export function isLevelColumnVisible(
  config: ReportDisplayConfig | null | undefined,
  levelKey: string,
  columnKey: string,
  defaultVisible: boolean,
): boolean {
  return isColumnVisibleByKeys(config, levelConfigKeys(levelKey), columnKey, defaultVisible);
}

/** Schlüssel des identifizierenden (nie ausblendbaren) Werts je Ebene. */
export function identifyingColumnKey(levelKey: string): string {
  if (levelKey === 'files') return 'fileName';
  if (levelKey === 'rules' || levelKey === 'specifications') return 'name';
  if (levelKey === 'structure') return 'identifikator';
  return '';
}

/**
 * ALLE sichtbaren Nicht-Status-Spalten einer Ebene in exakt gespeicherter Config-Reihenfolge –
 * inklusive der identifizierenden Spalte an IHRER konfigurierten Position (Status bleibt separat
 * die führende Icon-Spalte). Ohne Config (Altbericht) Fallback = [identifizierend, Ergebnis].
 */
export function orderedColumnKeys(
  config: ReportDisplayConfig | null | undefined,
  levelKey: string,
): string[] {
  const cols = findConfiguredLevel(config, configKeyForLevel(config, levelKey))?.columns ?? [];
  const keys = cols
    .filter(col => col.visible !== false && col.key !== 'status')
    .map(col => col.key);
  return keys.length > 0 ? keys : [identifyingColumnKey(levelKey), 'rating'];
}

/**
 * Konfigurierte, sichtbare Zusatzspalten einer Ebene (ohne Status und ohne den identifizierenden
 * Wert) in gespeicherter Reihenfolge. Ohne Config leer → nur identifizierender Wert + Status.
 */
export function secondaryColumnKeys(
  config: ReportDisplayConfig | null | undefined,
  levelKey: string,
): string[] {
  const cols = findConfiguredLevel(config, configKeyForLevel(config, levelKey))?.columns ?? [];
  const idKey = identifyingColumnKey(levelKey);
  return cols
    .filter(col => col.visible !== false && col.key !== 'status' && col.key !== idKey)
    .map(col => col.key);
}

/** Überschrift einer Ebene (konfiguriert, sonst Default-Label). */
export function levelLabel(
  config: ReportDisplayConfig | null | undefined,
  levelKey: string,
): string {
  const configured = findConfiguredLevel(config, configKeyForLevel(config, levelKey));
  return configured?.label || LEVEL_DEFAULT_LABELS[levelKey] || levelKey;
}

/** Überschrift einer Ebene über mehrere Kandidaten-Keys (IDS-Aliasse). */
export function levelLabelByKeys(
  config: ReportDisplayConfig | null | undefined,
  levelKeys: string[],
  fallback: string,
): string {
  return findConfiguredLevelByKeys(config, levelKeys)?.label || fallback;
}

/** Überschrift einer Spalte (konfiguriert, sonst Default-Label des Keys). */
export function columnLabel(
  config: ReportDisplayConfig | null | undefined,
  levelKey: string,
  columnKey: string,
): string {
  const col = findConfiguredLevel(config, configKeyForLevel(config, levelKey))
    ?.columns?.find(candidate => candidate.key === columnKey);
  return col?.label || COLUMN_DEFAULT_LABELS[columnKey] || columnKey;
}

/**
 * Darstellbare Spalten einer IDS-Ebene in konfigurierter Reihenfolge. Spalten, die in der
 * Konfiguration fehlen, bleiben (wie bei der Sichtbarkeit) in Default-Reihenfolge sichtbar;
 * die Status-Spalte wird über das Zeilen-Icon abgebildet.
 */
export function orderedVisibleIdsColumns(
  config: ReportDisplayConfig | null | undefined,
  levelKeys: string[],
  defaults: string[],
): string[] {
  const level = findConfiguredLevelByKeys(config, levelKeys);
  if (!level?.columns?.length) return defaults;

  const known = new Set(defaults);
  const mentioned = new Set(level.columns.map(column => column.key));
  const ordered = level.columns
    .filter(column => known.has(column.key) && column.visible !== false)
    .map(column => column.key);
  for (const key of defaults) {
    if (!mentioned.has(key)) ordered.push(key);
  }
  return ordered;
}
